import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, AlertTriangle, HelpCircle, Send } from "lucide-react";

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ type: "support", subject: "", description: "", target_user_id: "", priority: "normal" });
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/tickets");
    setItems(data);
  }, []);

  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/tickets", { ...form, target_user_id: form.target_user_id || null });
      setForm({ type: "support", subject: "", description: "", target_user_id: "", priority: "normal" });
      setCreating(false);
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Support & Reports</div>
          <h1 className="font-display text-4xl tracking-tight mt-2">Tickets</h1>
          <p className="text-white/60 text-sm mt-2">Eröffne ein Support-Ticket oder melde einen User.</p>
        </div>
        <button onClick={() => setCreating((c) => !c)} className="btn-primary" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4" /> Neues Ticket
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="card-solid p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Typ</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="ticket-type">
              <option value="support">Support</option>
              <option value="report">User Report</option>
            </select>
          </div>
          <div>
            <label className="label">Priorität</label>
            <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ticket-priority">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Betreff</label>
            <input required className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" />
          </div>
          {form.type === "report" && (
            <div className="md:col-span-2">
              <label className="label">Gemeldeter User</label>
              <select className="input" value={form.target_user_id} onChange={(e) => setForm({ ...form, target_user_id: e.target.value })} data-testid="ticket-target">
                <option value="">— wählen —</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.member_id})</option>)}
              </select>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="label">Beschreibung</label>
            <textarea required rows={4} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-description" />
          </div>
          {error && <div className="md:col-span-2 text-red-400 text-sm font-mono">{error}</div>}
          <div className="md:col-span-2">
            <button type="submit" className="btn-primary" data-testid="ticket-submit">Absenden</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            Keine Tickets.
          </div>
        ) : (
          items.map((t) => (
            <Link key={t.id} to={`/support/${t.id}`} className="card-solid p-4 flex items-center gap-4 hover:border-primary/40 transition-colors" data-testid={`ticket-${t.id}`}>
              {t.type === "report" ? <AlertTriangle className="w-5 h-5 text-magenta" /> : <HelpCircle className="w-5 h-5 text-primary" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-base tracking-tight truncate">{t.subject}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">
                  {t.type} · {t.status} · prio: {t.priority}
                </div>
              </div>
              <span className="font-mono text-[10px] text-white/40 uppercase tracking-[0.2em]">
                {new Date(t.created_at).toLocaleDateString("de-DE")}
              </span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

export function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setT(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    await api.post(`/tickets/${id}/messages`, { content: reply });
    setReply("");
    load();
  };

  const setStatus = async (status) => {
    await api.put(`/tickets/${id}/status`, { status });
    load();
  };

  if (error) return <div className="text-red-400 text-sm font-mono">{error}</div>;
  if (!t) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="ticket-detail-page">
      <Link to="/support" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Tickets</Link>
      <div className="card-solid p-6 tracing-border">
        <div className="flex items-center gap-2 mb-2">
          <span className="chip text-gradient">{t.type}</span>
          <span className="chip text-white/50">{t.status}</span>
          <span className="chip text-white/50">prio: {t.priority}</span>
        </div>
        <h1 className="font-display text-2xl tracking-tight">{t.subject}</h1>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
          Eröffnet von {t.reporter_name} · {new Date(t.created_at).toLocaleString("de-DE")}
          {t.target_name && <> · gegen {t.target_name}</>}
        </div>
        <div className="mt-4 text-white/80 whitespace-pre-wrap">{t.description}</div>
      </div>

      {user?.role === "admin" && (
        <div className="flex gap-2 flex-wrap">
          {["open", "in_progress", "resolved", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`set-status-${s}`}
              className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-[0.2em] font-mono border transition-colors ${
                t.status === s ? "bg-primary/10 border-primary text-primary" : "border-white/10 text-white/60 hover:border-primary/40"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {(t.messages || []).map((m) => (
          <div key={m.id} className={`p-3 rounded-sm ${m.is_admin ? "bg-magenta/10 border border-magenta/30" : "bg-white/5 border border-white/10"}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                {m.sender_name} {m.is_admin && <span className="text-magenta">· Admin</span>}
              </div>
              <div className="font-mono text-[10px] text-white/40">{new Date(m.created_at).toLocaleString("de-DE")}</div>
            </div>
            <div className="text-sm text-white/90 whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="flex gap-2">
        <input className="input flex-1" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Antwort schreiben…" data-testid="ticket-reply-input" />
        <button type="submit" className="btn-primary" data-testid="ticket-reply-submit">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
